import {
  RecordContextProvider,
  ResourceContextProvider,
  useGetList,
  useGetOne,
} from "ra-core";
import { Link } from "react-router";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { LoadWorkflowButtons } from "../loads/LoadWorkflowButtons";
import { useCurrentSale } from "../loads/useCurrentSale";
import { LOAD_STATUS_CHOICES } from "../loads/LoadInputs";
import { NoteAttachments } from "../notes/NoteAttachments";
import type { LoadNote } from "../types";
import type { DashboardLoad } from "./DashboardLoadCard";

type LoadDetail = DashboardLoad & {
  sales_owner_name?: string;
  purchasing_owner_name?: string;
  logistics_owner_name?: string;
  origin_location?: string;
  destination_location?: string;
  booking_number?: string;
  container_number?: string;
  seal_number?: string;
  hold_reason?: string;
  internal_notes?: string;
  vendor_reference_number?: string;
  customer_reference_number?: string;
};

const DetailField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="min-w-0">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="mt-1 break-words text-sm">{children || "—"}</dd>
  </div>
);

export const LoadWorkbenchDetail = ({ id }: { id: string }) => {
  const sale = useCurrentSale();
  const recordQuery = useGetOne<LoadDetail>("loads", { id });
  const summaryQuery = useGetOne<LoadDetail>("loads_summary", { id });
  const notes = useGetList<LoadNote>("load_notes", {
    pagination: { page: 1, perPage: 5 },
    sort: { field: "date", order: "DESC" },
    filter: { "load_id@eq": id, "deleted@eq": false },
  });

  if (recordQuery.error || summaryQuery.error)
    return (
      <p role="alert" className="p-6">
        Unable to open this load. It may no longer be available.
      </p>
    );
  if (recordQuery.isPending || summaryQuery.isPending)
    return <p className="p-6 text-muted-foreground">Loading load details…</p>;
  if (!recordQuery.data || !summaryQuery.data) return null;
  const load = { ...recordQuery.data, ...summaryQuery.data };
  const status =
    LOAD_STATUS_CHOICES.find(({ id }) => id === load.status)?.name ??
    load.status;

  return (
    <ResourceContextProvider value="loads">
      <RecordContextProvider value={load}>
        <div className="space-y-6 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold">Load {load.load_number}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{status}</p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to={`/loads/${id}/show`}>Open full record</Link>
            </Button>
          </div>
          {load.hold_reason && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              <span className="font-semibold">Hold reason: </span>
              {load.hold_reason}
            </div>
          )}
          <section className="space-y-3" aria-label="Workflow actions">
            <h4 className="text-sm font-semibold">Workflow actions</h4>
            <LoadWorkflowButtons sale={sale} />
          </section>
          <dl className="grid grid-cols-2 gap-4 border-t pt-5">
            <DetailField label="Vendor">
              {load.vendor_company_id ? (
                <Link
                  className="text-primary hover:underline"
                  to={`/companies/${load.vendor_company_id}/show`}
                >
                  {load.vendor_company_name || "Open vendor"}
                </Link>
              ) : (
                load.vendor_company_name
              )}
            </DetailField>
            <DetailField label="Customer">
              {load.customer_company_id ? (
                <Link
                  className="text-primary hover:underline"
                  to={`/companies/${load.customer_company_id}/show`}
                >
                  {load.customer_company_name || "Open customer"}
                </Link>
              ) : (
                load.customer_company_name
              )}
            </DetailField>
            <DetailField label="Pickup date">
              {load.pickup_date
                ? new Date(`${load.pickup_date}T00:00:00`).toLocaleDateString()
                : null}
            </DetailField>
            <DetailField label="Commodity">{load.main_commodity}</DetailField>
            <DetailField label="Profit (USD)">
              {load.profit_usd == null
                ? null
                : new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(load.profit_usd)}
            </DetailField>
            <DetailField label="Margin">
              {load.margin == null || load.margin === ""
                ? null
                : `${Number(load.margin).toFixed(1)}%`}
            </DetailField>
            <DetailField label="Origin">{load.origin_location}</DetailField>
            <DetailField label="Destination">
              {load.destination_location}
            </DetailField>
            <DetailField label="Shipment method">
              {load.shipment_method}
            </DetailField>
            <DetailField label="Freight terms">
              {load.freight_terms}
            </DetailField>
            <DetailField label="Booking #">{load.booking_number}</DetailField>
            <DetailField label="Container #">
              {load.container_number}
            </DetailField>
            <DetailField label="Seal #">{load.seal_number}</DetailField>
            <DetailField label="Vendor reference">
              {load.vendor_reference_number}
            </DetailField>
            <DetailField label="Customer reference">
              {load.customer_reference_number}
            </DetailField>
          </dl>
          <section className="border-t pt-5">
            <h4 className="mb-3 text-sm font-semibold">Assigned owners</h4>
            <dl className="grid grid-cols-2 gap-4">
              <DetailField label="Sales">{load.sales_owner_name}</DetailField>
              <DetailField label="Purchasing">
                {load.purchasing_owner_name}
              </DetailField>
              <DetailField label="Logistics">
                {load.logistics_owner_name}
              </DetailField>
            </dl>
          </section>
          {load.internal_notes && (
            <section className="border-t pt-5">
              <h4 className="mb-2 text-sm font-semibold">Internal notes</h4>
              <p className="whitespace-pre-wrap break-words text-sm">
                {load.internal_notes}
              </p>
            </section>
          )}
          <section className="space-y-3 border-t pt-5">
            <h4 className="text-sm font-semibold">Recent notes</h4>
            {notes.error ? (
              <p role="alert" className="text-sm text-destructive">
                Unable to load notes.
              </p>
            ) : notes.isPending ? (
              <p className="text-sm text-muted-foreground">Loading notes…</p>
            ) : !notes.data?.length ? (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            ) : (
              notes.data.map((note) => (
                <article
                  key={note.id}
                  className="rounded-lg border p-3 text-sm"
                >
                  <div className="mb-2 flex flex-wrap justify-between gap-2">
                    <span className="font-medium">
                      {note.subject || "Note"}
                    </span>
                    <time className="text-xs text-muted-foreground">
                      {note.date
                        ? new Date(note.date).toLocaleDateString()
                        : ""}
                    </time>
                  </div>
                  <p className="whitespace-pre-wrap break-words">{note.text}</p>
                  {note.attachments?.length ? (
                    <div className="overflow-x-auto">
                      <NoteAttachments note={note} />
                    </div>
                  ) : null}
                </article>
              ))
            )}
          </section>
        </div>
      </RecordContextProvider>
    </ResourceContextProvider>
  );
};
